<template>
  <div class="wave-config">
    <h2 class="mb-3">Wave Configuration</h2>

    <div class="alert alert-info mb-4">
      <strong>Note:</strong> eShopCommand uses Wave's free tier with static API tokens.
      Enter your Wave Business ID and API token below.
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-md-8">
        <form @submit.prevent="saveConfig">
          <div class="mb-3">
            <label for="businessId" class="form-label">Wave Business ID *</label>
            <input
              id="businessId"
              v-model="config.WAVE_BUSINESS_ID"
              type="text"
              class="form-control"
              required
            />
            <small class="form-text text-muted">
              Your Wave Business ID (e.g., QnVzaW5lc3M6...)
            </small>
          </div>

          <div class="mb-3">
            <label for="accessToken" class="form-label">Wave Access Token *</label>
            <transition mode="out-in">
              <div v-if="showAccessToken">
                <input
                    id="accessToken"
                    v-model="config.WAVE_ACCESS_TOKEN"
                    type="text"
                    class="form-control"
                    required
                />
              </div>
              <div v-else>
                <span class="text-info">•••••••••••••••••••</span>
                <button type="button" class="btn btn-sm btn-link" @click="fetchAccessToken">edit</button>
              </div>
            </transition>

            <small class="form-text text-muted">
              Your Wave API access token
            </small>
          </div>

          <hr class="my-4" />

          <h4>Account Mapping</h4>
          <p class="text-muted">Map your eBay transaction types to Wave accounts</p>

          <div class="mb-3">
            <label for="anchorAccount" class="form-label">Anchor Account (eBay Managed Payments)</label>
            <input
              id="anchorAccount"
              v-model="config.WAVE_ANCHOR_ACCOUNT_ID"
              type="text"
              class="form-control"
              placeholder="e.g., QWNjb3VudDo..."
            />
          </div>

          <div class="mb-3">
            <label for="salesAccount" class="form-label">Sales Account</label>
            <input
              id="salesAccount"
              v-model="config.WAVE_SALES_ACCOUNT_ID"
              type="text"
              class="form-control"
            />
          </div>

          <div class="mb-3">
            <label for="shippingAccount" class="form-label">Shipping Account</label>
            <input
              id="shippingAccount"
              v-model="config.WAVE_SHIPPING_ACCOUNT_ID"
              type="text"
              class="form-control"
            />
          </div>

          <div class="mb-3">
            <label for="feesAccount" class="form-label">eBay Fees Account</label>
            <input
              id="feesAccount"
              v-model="config.WAVE_EBAY_FEES_ACCOUNT_ID"
              type="text"
              class="form-control"
            />
          </div>

          <div class="mb-3">
            <label for="adFeesAccount" class="form-label">Ad Fees Account</label>
            <input
              id="adFeesAccount"
              v-model="config.WAVE_AD_FEES_ACCOUNT_ID"
              type="text"
              class="form-control"
            />
          </div>

          <div class="mb-3">
            <label for="cogsAccount" class="form-label">COGS Account</label>
            <input
              id="cogsAccount"
              v-model="config.WAVE_COGS_ACCOUNT_ID"
              type="text"
              class="form-control"
            />
          </div>

          <div class="mb-3">
            <label for="inventoryAccount" class="form-label">Inventory Account</label>
            <input
              id="inventoryAccount"
              v-model="config.WAVE_INVENTORY_ACCOUNT_ID"
              type="text"
              class="form-control"
            />
          </div>

          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ saving ? 'Saving...' : 'Save Configuration' }}
            </button>
            <button type="button" class="btn btn-outline-secondary" @click="loadConfig">
              Reset
            </button>
          </div>
        </form>

        <div v-if="saveMessage" class="alert mt-3" :class="saveSuccess ? 'alert-success' : 'alert-danger'">
          {{ saveMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const loading = ref(true)
const saving = ref(false)
const saveMessage = ref('')
const saveSuccess = ref(false)
const showAccessToken = ref(null)

const config = ref({
  WAVE_BUSINESS_ID: '',
  WAVE_ACCESS_TOKEN: '',
  WAVE_ANCHOR_ACCOUNT_ID: '',
  WAVE_SALES_ACCOUNT_ID: '',
  WAVE_SHIPPING_ACCOUNT_ID: '',
  WAVE_EBAY_FEES_ACCOUNT_ID: '',
  WAVE_AD_FEES_ACCOUNT_ID: '',
  WAVE_COGS_ACCOUNT_ID: '',
  WAVE_INVENTORY_ACCOUNT_ID: ''
})

async function loadConfig() {
  loading.value = true
  try {
    const response = await store.state.api.get('/plugins/wave/config')
    Object.assign(config.value, response.data.config)
  } catch (error) {
    console.error('Failed to load config:', error)
  } finally {
    loading.value = false
  }
}

async function fetchAccessToken() {
  // TODO get access token and show form
  showAccessToken.value = true
}

async function saveConfig() {
  saving.value = true
  saveMessage.value = ''

  try {
    // Save each config key
    for (const [key, value] of Object.entries(config.value)) {
      if (value) {
        await store.state.api.post('/plugins/wave/config', { key, value })
      }
    }

    saveSuccess.value = true
    saveMessage.value = 'Configuration saved successfully!'

    setTimeout(() => {
      saveMessage.value = ''
    }, 3000)
  } catch (error) {
    saveSuccess.value = false
    saveMessage.value = error.response?.data?.error || 'Failed to save configuration'
  } finally {
    saving.value = false
  }
}

onMounted(loadConfig)
</script>
